
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { SignupFormField, SignupFormConfig } from '@/schemas/signupFormSchema';
import { useToast } from '@/hooks/use-toast';
import { 
  createDynamicSchema, 
  createDefaultValues, 
  ensureFieldProperties
} from '@/utils/formUtils';

interface UseDynamicFormProps {
  onSubmit: (data: Record<string, any>) => void;
  formConfig: SignupFormConfig;
}

export const useDynamicForm = ({ onSubmit, formConfig }: UseDynamicFormProps) => {
  const [captchaText, setCaptchaText] = useState('');
  const { toast } = useToast();
  
  // Ensure all fields have proper properties and IDs by mapping through them
  // This guarantees that all fields conform to the SignupFormField type
  const formFieldsWithProperties: SignupFormField[] = formConfig.fields.map(field => 
    ensureFieldProperties(field)
  );
  
  // Create a new formConfig with validated fields
  const formConfigWithValidFields = {
    ...formConfig,
    fields: formFieldsWithProperties
  };
  
  // Create dynamic schema based on the form configuration
  const dynamicSchema = createDynamicSchema(formConfigWithValidFields.fields);
  
  // Create default values for the form
  const defaultValues = createDefaultValues(formConfigWithValidFields.fields);
  
  // Define the form with dynamic schema and defaults
  const form = useForm({
    resolver: zodResolver(dynamicSchema),
    defaultValues,
  });
  
  // Handle form submission with CAPTCHA validation
  const handleSubmit = async (data: Record<string, any>) => {
    // Validate CAPTCHA
    if (data.captcha !== captchaText) {
      form.setError("captcha", { 
        type: "manual", 
        message: "CAPTCHA verification failed. Please try again." 
      });
      return;
    }
    
    // Add timestamp if configured
    if (formConfigWithValidFields.includeTimestamp) {
      data.submissionTimestamp = new Date().toISOString();
    }
    
    // Add CC email if enabled
    if (formConfigWithValidFields.enableCcEmail) {
      data.ccEmail = formConfigWithValidFields.ccEmail;
    }
    
    // Continue with the submission
    onSubmit(data);
  };
  
  // Get visible fields only
  const visibleFields = formConfigWithValidFields.fields.filter(field => field.visible && field.type !== 'hidden');
  
  // Update captcha text when generated
  const handleCaptchaGenerate = (text: string) => {
    setCaptchaText(text);
  };
  
  return {
    form,
    handleSubmit,
    visibleFields,
    handleCaptchaGenerate
  };
};
